import { supabase } from '../auth/supabaseClient';
import seedData from './seedData';

const API_BASE = process.env.REACT_APP_API_BASE || '/api';
const DEFAULT_MODEL = 'porsche-boxster-986-1997-2004';

const normalizePartNumber = (value) => String(value || '').replace(/[\s.\-_/]/g, '').toUpperCase();
const textValue = (value) => String(value || '').toLowerCase();
const positionValue = (value) => {
  const raw = String(value ?? '').trim();
  return /^\(.*\)$/.test(raw) ? raw.slice(1, -1).trim() : raw;
};
const comparePartPositions = (left, right) => {
  const positionOrder = positionValue(left?.position).localeCompare(
    positionValue(right?.position),
    undefined,
    { numeric: true, sensitivity: 'base' }
  );
  if (positionOrder) return positionOrder;
  const leftParenthesized = /^\(.*\)$/.test(String(left?.position || '').trim());
  const rightParenthesized = /^\(.*\)$/.test(String(right?.position || '').trim());
  if (leftParenthesized !== rightParenthesized) return leftParenthesized ? 1 : -1;
  return String(left?.partNumber || '').localeCompare(String(right?.partNumber || ''), undefined, { numeric: true });
};
const CONVERTED_PNG_MODELS = new Set([
  'porsche-356-356-1950-1959',
  'porsche-356-356-1960-1965',
  'porsche-911-911-1965-1969',
  'porsche-911-911-1970-1973',
  'porsche-911-911-1974-1977',
  'porsche-911-911-1978-1983',
  'porsche-911-911-1984-1986',
  'porsche-911-911-1987-1989',
  'porsche-911-911t-1976-1977',
  'porsche-911-964-1989-1994',
  'porsche-911-991-gt3-2014',
  'porsche-911-993-1994-1998',
  'porsche-911-996-1999-2005',
  'porsche-911-997-2005-2008',
  'porsche-911-997-2009-2011',
  'porsche-911-997t-2007-2009',
  'porsche-911-997t2-2010-2011',
  'porsche-911-gt31-2003-2005',
  'porsche-911-gt32-2007-2011',
  'porsche-912-912-1965-1969',
  'porsche-914-914-unknown-unknown',
  'porsche-924-924-1976-1979',
  'porsche-924-924-1980-1985',
  'porsche-928-928-1978-1982',
  'porsche-928-928-1983-1986',
  'porsche-928-928-1987-1991',
  'porsche-928-928-1992-1995',
  'porsche-944-944-1982-1985',
  'porsche-944-944-1985-5-1988',
  'porsche-944-9442-1989-1991',
  'porsche-968-968-1992-1995',
  'porsche-boxster-986-1997-2004',
  'porsche-boxster-987-2005-2008',
  'porsche-boxster-987-2009-2011',
  'porsche-cayenne-92a-2011-2017',
  'porsche-cayenne-9pa-2003-2006',
  'porsche-cayenne-9pa-2007-2010',
  'porsche-cayman-987c-2006-2008',
  'porsche-cayman-987c2-2009-2011',
  'porsche-panamera-970-2010-2011',
]);

const normalizeDiagramAsset = (diagram) => {
  if (!diagram || !CONVERTED_PNG_MODELS.has(diagram.modelSlug)) return diagram;
  const localPng = `/assets/parts/porsche-diagrams/${diagram.modelSlug}/${diagram.sectionCode}.png`;
  return {
    ...diagram,
    imageUrl: localPng,
    localImagePath: localPng,
  };
};

const normalizeDiagramPayload = (payload) => ({
  ...payload,
  diagrams: (payload.diagrams || []).map(normalizeDiagramAsset),
});

const normalizeSingleDiagramPayload = (payload) => ({
  ...payload,
  diagram: normalizeDiagramAsset(payload.diagram),
  parts: [...(payload.parts || [])].sort(comparePartPositions),
});

const normalizeSearchPayload = (payload) => ({
  ...payload,
  results: (payload.results || []).map((result) => ({
    ...result,
    diagram: normalizeDiagramAsset(result.diagram),
  })),
});

const SYNONYMS = {
  rotors: ['rotor', 'brake disc', 'disc'],
  rotor: ['rotors', 'brake disc', 'disc'],
  brakes: ['brake', 'rotor', 'pad', 'caliper', 'calliper', 'disc'],
  brake: ['brakes', 'rotor', 'pad', 'caliper', 'calliper', 'disc'],
  caliper: ['calliper', 'brake caliper', 'brake calliper'],
  calipers: ['callipers', 'brake calipers', 'brake callipers'],
  calliper: ['caliper', 'brake caliper', 'brake calliper'],
  callipers: ['calipers', 'brake calipers', 'brake callipers'],
  coils: ['coil', 'ignition coil'],
  headlight: ['headlamp', 'lamp', 'lighting'],
  headlamp: ['headlight', 'lamp', 'lighting'],
  coolant: ['cooling', 'water pump', 'thermostat', 'hose'],
  shifter: ['gearshift', 'shift linkage', 'cable'],
  axle: ['drive shaft', 'driveshaft', 'drive-shaft'],
  axles: ['drive shaft', 'driveshaft', 'drive-shaft'],
};

export const expandQueryTerms = (query) => {
  const terms = String(query || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  const expanded = new Set(terms);
  terms.forEach((term) => (SYNONYMS[term] || []).forEach((syn) => expanded.add(syn)));
  return Array.from(expanded);
};

const fallbackData = () => ({
  models: seedData.models || [],
  diagrams: seedData.diagrams || [],
  parts: (seedData.parts || []).map((part) => ({
    ...part,
    normalizedPartNumber: normalizePartNumber(part.partNumber),
  })),
  videos: seedData.videos || [],
  mappings: seedData.mappings || [],
});

const attachVideosToPart = (part, data) => {
  const ids = new Set(part.relatedVideoIds || []);
  data.mappings.forEach((mapping) => {
    const samePart = mapping.partNumber && normalizePartNumber(mapping.partNumber) === normalizePartNumber(part.partNumber);
    const sameSectionPosition = mapping.sectionCode === part.sectionCode && mapping.position && mapping.position === part.position;
    if (samePart || sameSectionPosition) ids.add(mapping.videoId);
  });
  return Array.from(ids).map((id) => data.videos.find((video) => video.videoId === id)).filter(Boolean);
};

const attachVideosToDiagram = (diagram, data) => {
  const ids = new Set(diagram.relatedVideoIds || []);
  data.mappings.forEach((mapping) => {
    if (mapping.sectionCode === diagram.sectionCode && mapping.modelSlug === diagram.modelSlug) ids.add(mapping.videoId);
  });
  return Array.from(ids).map((id) => data.videos.find((video) => video.videoId === id)).filter(Boolean);
};

const scoreDiagram = (diagram, query, terms) => {
  if (!query) return 1;
  let score = 0;
  const haystack = [diagram.sectionCode, diagram.title, diagram.category, ...(diagram.keywords || [])].map(textValue).join(' ');
  if (textValue(diagram.sectionCode) === textValue(query)) score += 100;
  if (textValue(diagram.category) === textValue(query)) score += 45;
  terms.forEach((term) => {
    if (haystack.includes(term)) score += term.length > 3 ? 10 : 4;
  });
  return score;
};

export const scorePart = (part, diagram, query, terms, normalizedQuery) => {
  if (!query) return 1;
  let score = 0;
  const normalizedPart = normalizePartNumber(part.partNumber);
  const queryText = textValue(query);
  const description = textValue(part.description);
  const fitmentNotes = textValue(part.fitmentNotes);
  const contextualHaystack = [
    part.sectionCode,
    diagram?.title,
    diagram?.category,
    ...(part.keywords || []),
    ...(diagram?.keywords || []),
  ].map(textValue).join(' ');
  if (normalizedQuery && normalizedPart === normalizedQuery) score += 120;
  if (normalizedQuery && normalizedPart.includes(normalizedQuery)) score += 80;
  if (description === queryText) score += 100;
  else if (description.startsWith(queryText)) score += 75;
  else if (description.includes(queryText)) score += 60;
  if (textValue(part.sectionCode) === queryText) score += 70;
  if (textValue(diagram?.category) === queryText) score += 50;
  terms.forEach((term) => {
    if (description.includes(term)) score += term === queryText ? 30 : 20;
    else if (contextualHaystack.includes(term)) score += term.length > 3 ? 10 : 4;
    else if (fitmentNotes.includes(term)) score += term.length > 3 ? 3 : 1;
  });
  return score;
};

const fetchStaticJson = async (url) => {
  const response = await fetch(url, { credentials: 'omit' });
  if (!response.ok) throw new Error(`Static parts data not found: ${url}`);
  return response.json();
};

const fallbackModels = async () => {
  try {
    const manifest = await fetchStaticJson('/assets/parts/data/manifest.json');
    return { models: manifest.models || [], offline: true };
  } catch (error) {
    return { models: fallbackData().models, offline: true };
  }
};

const fallbackDiagrams = async ({ modelSlug = DEFAULT_MODEL, category } = {}) => {
  try {
    const manifest = await fetchStaticJson('/assets/parts/data/manifest.json');
    let diagrams = (manifest.diagrams || []).filter((diagram) => diagram.modelSlug === modelSlug);
    if (category) diagrams = diagrams.filter((diagram) => textValue(diagram.category) === textValue(category));
    return { diagrams: diagrams.map(normalizeDiagramAsset), offline: true };
  } catch (error) {
    const data = fallbackData();
    let diagrams = data.diagrams.filter((diagram) => diagram.modelSlug === modelSlug);
    if (category) diagrams = diagrams.filter((diagram) => textValue(diagram.category) === textValue(category));
    return {
      diagrams: diagrams.map((diagram) => normalizeDiagramAsset({
        ...diagram,
        partCount: data.parts.filter((part) => part.modelSlug === diagram.modelSlug && part.sectionCode === diagram.sectionCode).length,
        videos: attachVideosToDiagram(diagram, data),
      })),
      offline: true,
    };
  }
};

const fallbackDiagram = async ({ modelSlug = DEFAULT_MODEL, sectionCode }) => {
  const staticUrl = `/assets/parts/data/${encodeURIComponent(modelSlug)}/${encodeURIComponent(sectionCode)}.json`;
  try {
    return await fetchStaticJson(staticUrl);
  } catch (error) {
    // Fall through to the small bundled fallback below.
  }
  const data = fallbackData();
  const diagram = data.diagrams.find((item) => item.modelSlug === modelSlug && item.sectionCode === sectionCode);
  if (!diagram) throw new Error('Diagram not found in offline fallback data.');
  const parts = data.parts
    .filter((part) => part.modelSlug === modelSlug && part.sectionCode === sectionCode)
    .map((part) => ({ ...part, videos: attachVideosToPart(part, data) }));
  return { diagram: normalizeDiagramAsset({ ...diagram, videos: attachVideosToDiagram(diagram, data) }), parts, offline: true };
};

const fallbackSearch = async ({ query, modelSlug = DEFAULT_MODEL, category, limit = 50 } = {}) => {
  const q = String(query || '').trim();
  const terms = expandQueryTerms(q);
  const normalizedQuery = normalizePartNumber(q);
  const data = fallbackData();
  const diagramsByKey = new Map(data.diagrams.map((diagram) => [`${diagram.modelSlug}:${diagram.sectionCode}`, diagram]));

  const partResults = data.parts
    .filter((part) => part.modelSlug === modelSlug)
    .filter((part) => {
      if (!category) return true;
      const diagram = diagramsByKey.get(`${part.modelSlug}:${part.sectionCode}`);
      return textValue(diagram?.category) === textValue(category);
    })
    .map((part) => {
      const diagram = diagramsByKey.get(`${part.modelSlug}:${part.sectionCode}`);
      return { type: 'part', score: scorePart(part, diagram, q, terms, normalizedQuery), part: { ...part, videos: attachVideosToPart(part, data) }, diagram: normalizeDiagramAsset(diagram) };
    })
    .filter((result) => result.score > 0);

  const diagramResults = data.diagrams
    .filter((diagram) => diagram.modelSlug === modelSlug)
    .filter((diagram) => !category || textValue(diagram.category) === textValue(category))
    .map((diagram) => ({ type: 'diagram', score: scoreDiagram(diagram, q, terms), diagram: normalizeDiagramAsset({ ...diagram, videos: attachVideosToDiagram(diagram, data) }) }))
    .filter((result) => result.score > 0);

  return { query: q, results: [...partResults, ...diagramResults].sort((a, b) => b.score - a.score).slice(0, limit), offline: true };
};

const fallbackFor = (fallback, error) => {
  console.warn('Parts API unavailable; using bundled Porsche 986 fallback data.', error);
  return fallback();
};

const requestJson = async (url, fallback) => {
  try {
    const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!response.ok) throw new Error(await response.text() || 'Parts lookup request failed');
    return response.json();
  } catch (error) {
    if (fallback) return fallbackFor(fallback, error);
    throw error;
  }
};

export const getPartModels = () => requestJson(`${API_BASE}/parts/models`, fallbackModels);

export const getPartnershipSettings = () =>
  requestJson(
    `${API_BASE}/parts/settings/partnerships`,
    () => ({ pelicanPartsLinksEnabled: false })
  );

export const getPartDiagrams = async ({ modelSlug, category } = {}) => {
  const resolvedModel = modelSlug || DEFAULT_MODEL;
  const params = new URLSearchParams();
  if (category) params.set('category', category);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  try {
    const payload = await requestJson(
      `${API_BASE}/parts/models/${resolvedModel}/diagrams${suffix}`,
      null
    );
    const diagrams = payload.diagrams || [];
    const hasAnyParts = diagrams.some((diagram) => Number(diagram.partCount || 0) > 0);
    if (diagrams.length && hasAnyParts) return normalizeDiagramPayload(payload);
    const staticPayload = await fallbackDiagrams({ modelSlug: resolvedModel, category });
    if ((staticPayload.diagrams || []).some((diagram) => Number(diagram.partCount || 0) > 0)) return normalizeDiagramPayload(staticPayload);
    return normalizeDiagramPayload(payload);
  } catch (error) {
    return fallbackFor(() => fallbackDiagrams({ modelSlug: resolvedModel, category }), error);
  }
};

export const getPartDiagram = async ({ modelSlug, sectionCode }) => {
  const resolvedModel = modelSlug || DEFAULT_MODEL;
  try {
    const payload = await requestJson(
      `${API_BASE}/parts/models/${resolvedModel}/diagrams/${sectionCode}`,
      null
    );
    if ((payload.parts || []).length > 0) return normalizeSingleDiagramPayload(payload);
    const staticPayload = await fallbackDiagram({ modelSlug: resolvedModel, sectionCode });
    if ((staticPayload.parts || []).length > 0) return normalizeSingleDiagramPayload(staticPayload);
    return normalizeSingleDiagramPayload(payload);
  } catch (error) {
    return fallbackFor(() => fallbackDiagram({ modelSlug: resolvedModel, sectionCode }), error);
  }
};

export const searchParts = async ({ query, modelSlug, category, limit = 50 }) => {
  const resolvedModel = modelSlug || DEFAULT_MODEL;
  const params = new URLSearchParams();
  if (query) params.set('q', query);
  if (resolvedModel) params.set('model', resolvedModel);
  if (category) params.set('category', category);
  params.set('limit', String(limit));
  try {
    const payload = await requestJson(`${API_BASE}/parts/search?${params.toString()}`, null);
    if ((payload.results || []).length > 0 || !String(query || '').trim()) return normalizeSearchPayload(payload);
    const staticPayload = await fallbackSearch({ query, modelSlug: resolvedModel, category, limit });
    if ((staticPayload.results || []).length > 0) return normalizeSearchPayload(staticPayload);
    return normalizeSearchPayload(payload);
  } catch (error) {
    return fallbackFor(() => fallbackSearch({ query, modelSlug: resolvedModel, category, limit }), error);
  }
};

const authHeaders = async () => {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Please sign in to continue.');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

const requestAuthJson = async (url, options = {}) => {
  const headers = await authHeaders();
  const response = await fetch(url, {
    ...options,
    mode: 'cors',
    credentials: 'omit',
    headers: { ...headers, ...(options.headers || {}) },
  });
  if (!response.ok) {
    const raw = await response.text();
    let payload = null;
    try { payload = JSON.parse(raw); } catch (_) {}
    const error = new Error(payload?.error || raw || 'Authenticated request failed');
    error.status = response.status;
    error.code = payload?.code;
    throw error;
  }
  return response.json();
};

const requestAdminJson = requestAuthJson;

export const updatePartnershipSettings = ({ pelicanPartsLinksEnabled }) =>
  requestAdminJson(`${API_BASE}/parts/admin/settings/partnerships`, {
    method: 'PUT',
    body: JSON.stringify({ pelicanPartsLinksEnabled }),
  });

export const getSubscriberAccess = () =>
  requestAuthJson(`${API_BASE}/parts/service-specs/access`);

export const getDiagramServiceSpecs = ({ modelSlug, sectionCode }) =>
  requestAuthJson(
    `${API_BASE}/parts/models/${encodeURIComponent(modelSlug)}/diagrams/${encodeURIComponent(sectionCode)}/service-specs`
  );

export const verifyYouTubeSubscription = ({ googleAccessToken, subscribe = false }) =>
  requestAuthJson(`${API_BASE}/parts/service-specs/verify-subscription`, {
    method: 'POST',
    body: JSON.stringify({ googleAccessToken, subscribe }),
  });

export const submitServiceSpecContribution = (payload) =>
  requestAuthJson(`${API_BASE}/parts/service-specs/submissions`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const getMyServiceSpecSubmissions = () =>
  requestAuthJson(`${API_BASE}/parts/service-specs/submissions/mine`);

export const getAdminServiceSpecSubmissions = ({ status = 'all' } = {}) =>
  requestAdminJson(`${API_BASE}/parts/admin/service-specs/submissions?status=${encodeURIComponent(status)}`);

export const getAdminServiceSpecs = () =>
  requestAdminJson(`${API_BASE}/parts/admin/service-specs`);

export const approveServiceSpecSubmission = ({ id, proposedSpecification, note = '' }) =>
  requestAdminJson(`${API_BASE}/parts/admin/service-specs/submissions/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
    body: JSON.stringify({ proposedSpecification, note }),
  });

export const rejectServiceSpecSubmission = ({ id, note = '' }) =>
  requestAdminJson(`${API_BASE}/parts/admin/service-specs/submissions/${encodeURIComponent(id)}/reject`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });

export const deleteRejectedServiceSpecSubmission = ({ id }) =>
  requestAdminJson(`${API_BASE}/parts/admin/service-specs/submissions/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });

export const updateAdminServiceSpec = ({ specKey, specification }) =>
  requestAdminJson(`${API_BASE}/parts/admin/service-specs/${encodeURIComponent(specKey)}`, {
    method: 'PUT',
    body: JSON.stringify(specification),
  });

export const searchHelpVideos = ({ query, limit = 25 } = {}) => {
  const params = new URLSearchParams();
  if (query) params.set('q', query);
  params.set('limit', String(limit));
  return requestAdminJson(`${API_BASE}/parts/admin/videos/search?${params.toString()}`);
};

export const addPartVideoMapping = ({ modelSlug, sectionCode, videoId, partNumber = '', position = '', reason }) =>
  requestAdminJson(`${API_BASE}/parts/admin/models/${encodeURIComponent(modelSlug)}/diagrams/${encodeURIComponent(sectionCode)}/videos`, {
    method: 'POST',
    body: JSON.stringify({ videoId, partNumber, position, reason }),
  });

export const removePartVideoMapping = ({ modelSlug, sectionCode, videoId, partNumber = '', position = '' }) => {
  const params = new URLSearchParams();
  if (partNumber) params.set('partNumber', partNumber);
  if (position) params.set('position', position);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return requestAdminJson(`${API_BASE}/parts/admin/models/${encodeURIComponent(modelSlug)}/diagrams/${encodeURIComponent(sectionCode)}/videos/${encodeURIComponent(videoId)}${suffix}`, {
    method: 'DELETE',
  });
};

